import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useChurch } from '../context/ChurchContext';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/common/ConfirmModal';
import CardRenderer from '../components/common/CardRenderer';
import { requiredMessage, apiFieldErrors, firstErrorMessage, FieldError, Req, ERROR_COLOR } from '../utils/formErrors';

const CARD = {
  background: 'var(--card-bg)', borderRadius: '14px', border: '1px solid var(--border)',
  boxShadow: 'var(--card-shadow)', padding: '18px',
};
const BTN_DARK = { background: '#1e293b', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: 700, cursor: 'pointer' };
const BTN_GHOST = { background: 'var(--page-bg)', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: '6px', fontWeight: 600, cursor: 'pointer' };

// Miniature réelle d'un template : rend le recto brouillon via CardRenderer,
// puis le réduit avec un scale CSS pour obtenir un vrai aperçu à l'échelle.
function TemplateThumb({ template, fieldChoices }) {
  const side = template.preview;
  const widthPx = 320;
  const heightPx = Math.round(320 * (template.height_mm / template.width_mm));
  const scale = 0.4; // affiché à ~128px de large
  if (!side || !side.elements?.length) {
    return (
      <div style={{ width: 128, height: 81, borderRadius: 6, border: '1px solid var(--border)', background: 'linear-gradient(135deg,#f8fafc,#e2e8f0)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 10, flexShrink: 0 }}>
        Vide
      </div>
    );
  }
  return (
    <div style={{ width: widthPx * scale, height: heightPx * scale, flexShrink: 0, position: 'relative', overflow: 'hidden', borderRadius: 5, border: '1px solid var(--border)', background: '#fff' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, transform: `scale(${scale})`, transformOrigin: 'top left', pointerEvents: 'none' }}>
        <CardRenderer side={side} widthPx={widthPx} heightPx={heightPx} fieldChoices={fieldChoices} />
      </div>
    </div>
  );
}

export default function CardTemplates() {
  const navigate = useNavigate();
  const { activeChurchId, isPlatformAdmin } = useChurch();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [nameError, setNameError] = useState('');
  const [newOrientation, setNewOrientation] = useState('landscape');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [creatingDefault, setCreatingDefault] = useState(false);
  const [creatingDefaultAll, setCreatingDefaultAll] = useState(false);
  const [search, setSearch] = useState('');
  const [fieldChoices, setFieldChoices] = useState([]);

  useEffect(() => {
    api.get('/cards/field-choices/').then(r => {
      if (r.data?.fields?.length) setFieldChoices(r.data.fields);
    }).catch(() => {});
  }, []);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params = isPlatformAdmin && activeChurchId ? { church_id: activeChurchId } : {};
      const res = await api.get('/cards/templates/', { params });
      const list = Array.isArray(res.data) ? res.data : res.data.results || [];
      setTemplates(list);
    } catch {
      toast.error('Erreur lors du chargement des templates.');
    } finally {
      setLoading(false);
    }
  }, [activeChurchId, isPlatformAdmin]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) { setNameError(requiredMessage('Nom du template')); return; }
    setNameError('');
    if (isPlatformAdmin && !activeChurchId) {
      toast.error("Sélectionnez une église dans la barre du haut avant de créer un template.");
      return;
    }
    setCreating(true);
    try {
      const fd = new FormData();
      fd.append('name', newName.trim());
      fd.append('orientation', newOrientation);
      // Format CR80 standard (85.6×54mm) inversé en portrait.
      fd.append('width_mm', newOrientation === 'portrait' ? '54' : '85.6');
      fd.append('height_mm', newOrientation === 'portrait' ? '85.6' : '54');
      const params = isPlatformAdmin ? { church_id: activeChurchId } : {};
      const res = await api.post('/cards/templates/', fd, { params, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Template créé.');
      setNewName('');
      setNewOrientation('landscape');
      navigate(`/cartes/${res.data.id}`);
    } catch (err) {
      const errors = apiFieldErrors(err, { name: 'Nom du template' });
      setNameError(errors.name || '');
      toast.error(firstErrorMessage(errors, { name: 'Nom du template' }, 'Erreur lors de la création.'));
    } finally {
      setCreating(false);
    }
  };

  const handleDuplicate = async (t) => {
    try {
      await api.post(`/cards/templates/${t.id}/duplicate/`);
      toast.success('Template dupliqué.');
      fetchTemplates();
    } catch {
      toast.error('Erreur lors de la duplication.');
    }
  };

  const handleSetDefault = async (t) => {
    try {
      await api.post(`/cards/templates/${t.id}/set-default/`);
      toast.success('Template défini par défaut.');
      fetchTemplates();
    } catch {
      toast.error('Erreur lors de la mise à jour.');
    }
  };

  const handleCreateDefault = async () => {
    if (isPlatformAdmin && !activeChurchId) {
      toast.error("Sélectionnez une église dans la barre du haut.");
      return;
    }
    setCreatingDefault(true);
    try {
      const params = isPlatformAdmin ? { church_id: activeChurchId } : {};
      await api.post('/cards/templates/create-default/', {}, { params });
      toast.success('Carte par défaut créée et publiée.');
      fetchTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.[0] || 'Erreur lors de la création de la carte par défaut.');
    } finally {
      setCreatingDefault(false);
    }
  };

  const handleCreateDefaultAll = async () => {
    setCreatingDefaultAll(true);
    try {
      const res = await api.post('/cards/templates/create-default-all/');
      if (res.data.count === 0) {
        toast.success('Toutes les églises ont déjà une carte par défaut.');
      } else {
        toast.success(`Carte par défaut créée pour ${res.data.count} église(s) : ${res.data.created.map(c => c.church_name).join(', ')}`);
      }
      fetchTemplates();
    } catch {
      toast.error('Erreur lors de la création des cartes par défaut.');
    } finally {
      setCreatingDefaultAll(false);
    }
  };

  const handleDelete = async (t) => {
    try {
      await api.delete(`/cards/templates/${t.id}/`);
      setTemplates(prev => prev.filter(x => x.id !== t.id));
      toast.success('Template supprimé.');
    } catch {
      toast.error('Erreur lors de la suppression.');
    } finally {
      setConfirmDelete(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(t => t.name.toLowerCase().includes(q));
  }, [templates, search]);

  return (
    <div style={{ width: '100%', maxWidth: '900px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>Cartes de membre</h1>
        <p style={{ color: 'var(--text-secondary)', marginTop: '4px', fontSize: '0.9rem' }}>
          Créez et personnalisez les modèles de carte de vos fidèles — disposition, champs, QR code.
        </p>
      </div>

      <div style={{ ...CARD, marginBottom: '16px' }}>
        <h2 style={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 12px' }}>Nouveau template</h2>
        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '6px' }}>Nom du template<Req /></label>
        <form onSubmit={handleCreate} noValidate style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Ex : Carte membre standard"
              style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: '7px', border: `1px solid ${nameError ? ERROR_COLOR : 'var(--border)'}`, fontSize: '0.86rem', background: '#fff', color: 'var(--text-primary)' }} />
            <FieldError message={nameError} />
          </div>
          <select value={newOrientation} onChange={e => setNewOrientation(e.target.value)}
            style={{ padding: '9px 12px', borderRadius: '7px', border: '1px solid var(--border)', fontSize: '0.86rem', background: '#fff', color: 'var(--text-primary)' }}>
            <option value="landscape">Paysage (85.6 × 54mm)</option>
            <option value="portrait">Portrait (54 × 85.6mm)</option>
          </select>
          <button type="submit" disabled={creating} style={{ ...BTN_DARK, padding: '9px 20px', fontSize: '0.85rem' }}>
            {creating ? 'Création...' : '+ Créer'}
          </button>
        </form>
      </div>

      {!loading && !templates.some(t => t.is_default && t.has_published_version) && (
        <div style={{ ...CARD, marginBottom: '16px', background: '#fff7ed', border: '1px solid #fed7aa', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.86rem', fontWeight: 700, color: '#9a3412' }}>⚠ Aucune carte par défaut publiée pour cette église</div>
            <div style={{ fontSize: '0.78rem', color: '#9a3412', marginTop: 2 }}>
              Sans carte par défaut, les fidèles sans template assigné n'auront aucune carte affichable.
            </div>
          </div>
          <button onClick={handleCreateDefault} disabled={creatingDefault}
            style={{ background: '#ea580c', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, cursor: 'pointer', padding: '9px 16px', fontSize: '0.82rem', flexShrink: 0 }}>
            {creatingDefault ? 'Création...' : '+ Créer une carte par défaut'}
          </button>
        </div>
      )}

      {isPlatformAdmin && (
        <div style={{ ...CARD, marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.86rem', fontWeight: 700, color: 'var(--text-primary)' }}>Toutes les églises</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 2 }}>
              Créer une carte par défaut pour chaque église de la plateforme qui n'en a pas encore, en une seule action.
            </div>
          </div>
          <button onClick={handleCreateDefaultAll} disabled={creatingDefaultAll} style={{ ...BTN_GHOST, padding: '9px 16px', fontSize: '0.82rem', flexShrink: 0 }}>
            {creatingDefaultAll ? 'Création...' : '+ Créer pour toutes les églises sans défaut'}
          </button>
        </div>
      )}

      <div style={{ ...CARD, marginBottom: '16px' }}>
        <div style={{ fontSize: '0.86rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
          Mes templates ({filtered.length})
        </div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Rechercher un template..."
          style={{ width: '100%', padding: '9px 12px', borderRadius: '7px', border: '1px solid var(--border)', fontSize: '0.86rem', background: '#fff', color: 'var(--text-primary)', boxSizing: 'border-box' }} />
      </div>

      <div style={CARD}>
        {loading ? (
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Chargement...</p>
        ) : filtered.length === 0 ? (
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{templates.length === 0 ? 'Aucun template pour le moment.' : 'Aucun template ne correspond à votre recherche.'}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {filtered.map(t => (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 14px', border: '1px solid var(--border)', borderRadius: '10px', background: 'var(--page-bg)' }}>
                <TemplateThumb template={t} fieldChoices={fieldChoices} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-primary)' }}>{t.name}</span>
                    {t.is_default && (
                      <span style={{ fontSize: '0.62rem', fontWeight: 800, color: '#fff', background: '#1e293b', borderRadius: 5, padding: '2px 7px' }}>PAR DÉFAUT</span>
                    )}
                    {t.has_published_version ? (
                      <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#166534', background: '#f0fdf4', borderRadius: 5, padding: '2px 7px' }}>PUBLIÉ</span>
                    ) : (
                      <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#9a3412', background: '#fff7ed', borderRadius: 5, padding: '2px 7px' }}>BROUILLON — jamais publié</span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                    {t.width_mm}mm × {t.height_mm}mm · {t.orientation === 'landscape' ? 'Paysage' : 'Portrait'}
                  </div>
                  <button onClick={() => navigate(`/cartes/${t.id}`)} style={{ ...BTN_DARK, padding: '6px 14px', fontSize: '0.74rem', marginTop: 8 }}>
                    ✏️ Personnaliser
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0 }}>
                  {!t.is_default && (
                    <button onClick={() => handleSetDefault(t)} style={{ ...BTN_GHOST, padding: '7px 10px', fontSize: '0.72rem' }}>
                      Définir par défaut
                    </button>
                  )}
                  <button onClick={() => handleDuplicate(t)} style={{ ...BTN_GHOST, padding: '7px 10px', fontSize: '0.72rem' }}>
                    Dupliquer
                  </button>
                  <button onClick={() => setConfirmDelete(t)} style={{ background: '#fef2f2', color: '#b91c1c', border: 'none', borderRadius: 6, padding: '7px 10px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}>
                    Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmModal
        open={!!confirmDelete}
        title="Supprimer ce template ?"
        message={`« ${confirmDelete?.name} » sera définitivement supprimé, ainsi que tous ses éléments.`}
        confirmLabel="Supprimer"
        onConfirm={() => handleDelete(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
